import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Router, Switch, Route, Redirect } from 'react-router-dom'
import { useIntl } from 'react-intl'
import SupplierDashboardPage from 'containers/SupplierDashboardPage'
import SupplierDataTab from 'rest/views/coordinator/supplier/data/Tab'
import SupplierProfile from 'containers/SupplierProfile'
import SupplierJobOverviewPage from 'containers/SupplierJobOverviewPage'
import UserSettingsPage from 'containers/UserSettingsPage'
import SidebarLayout from 'components/SidebarLayout'
import SupplierBookingsPage from 'containers/SupplierBookingsPage'
import SupplierInfoPage from 'containers/SupplierInfoPage'
import SupplierTaskPage from 'containers/SupplierTaskPage'
import useRoutes from 'hooks/useRoutes'
import useCurrentUser from 'hooks/useCurrentUser'
import { SupplierChat } from 'components/SupportChat'
import messages from './messages'
import TaskSearchPage from 'containers/TaskSearchPage'
import SupplierTaskRedirectPage from './containers/SupplierTaskRedirectPage'

import Nda from 'containers/SupplierProfile/components/Nda'
import NdaRequiredModal from 'components/NdaRequiredModal'
import NiceModal  from '@ebay/nice-modal-react'
import SupplierSyntheticVoicePage from 'containers/SupplierSyntheticVoicePage'
import PublicLiveSessionRedirectPage from 'containers/PublicLiveSessionRedirectPage'
import PublicSchedulingPollPage from 'containers/PublicSchedulingPollPage'
import PublicMemorySharingPage from 'containers/PublicMemorySharingPage'


const SupplierApp = ({ history, partner }) => {
  const [requireNda, setRequireNda] = useState(false)
  const [supplierId, setSupplierId] = useState(null)
  const [isProfileComplete, setProfileComplete] = useState(false)
  const { setRoutes, routes } = useRoutes()
  const { roles = [], features } = useCurrentUser()
  const { formatMessage } = useIntl()
  const interval = useRef(null)
  const modalShown = useRef(false)

  useEffect(() => {
    if (!supplierId) {
      fetch('/supplierviewdisplay/getCurrentSupplierId?getAll=0').then((response) => {
        return response.text();
      }).then(data => {
        setSupplierId(data)
      })
    } else {
      interval.current = setInterval(() => {
        fetch(`/rest/v2/supplier/${supplierId}/state`, { credentials: 'same-origin' })
          .then((response) => response.json())
          .then((response) => {
            if (!response?.ndaRequired) {
              setRequireNda(false)
              clearInterval(interval.current)
            } else if (response?.ndaRequired) {
              setRequireNda(true)
            }

            if (response?.stage && response?.stage === 'ONBOARDED') {
              setProfileComplete(true)
            }
          })
      }, 2000)
    }

    return () => {
      if (interval.current) {
        clearInterval(interval.current)
      }
    }
  }, [supplierId, requireNda])


  useEffect(() => {
    if (requireNda) {
      NiceModal.show(NdaRequiredModal)
    }
  }, [requireNda])

  const nextRoutes = useMemo(() => {
    if (routes.length) {
      return routes
    }

    let defaultRoutes = [
      {
        name: formatMessage(messages.navDashboard),
        path: '/dashboard',
        icon: 'house',
      },
      {
        name: formatMessage(messages.navNewJobs),
        path: '/jobs/new',
        icon: 'list',
      },
      {
        name: formatMessage(messages.navOpenJobs),
        path: '/jobs/open',
        icon: 'file-invoice',
      },
      {
        name: formatMessage(messages.navCompletedJobs),
        path: '/jobs/completed',
        icon: 'file-check',
      },
      {
        name: formatMessage(messages.navBookings),
        path: '/bookings',
        icon: 'calendar',
      },
      {
        name: formatMessage(messages.navInfo),
        path: '/docs',
        icon: 'info',
      },
      {
        name: formatMessage(messages.navProfile),
        path: '/talent/profile',
        icon: 'user',
      },
      {
        name: formatMessage(messages.navContact),
        path: '/reload-dashboard',
        href: formatMessage(messages.navContactHref),
        icon: 'at',
      },
    ]

    if (partner !== 'voicetome' && isProfileComplete && features.includes('ai-voice-clone')) {
      defaultRoutes.splice(defaultRoutes.length - 1, 0, { // Add second last in the list
        name: formatMessage(messages.navMyAiVoice),
        path: '/ai-voice-clone',
        icon: 'robot',
      })
    }

    if (roles.includes('post_producer')) {
      defaultRoutes.push({
        name: formatMessage(messages.navOverview),
        path: '/overview',
        icon: 'list-ol',
      })
    }
    
    return defaultRoutes
  }, [routes, roles, isProfileComplete])



  const renderWithSupplierId = (Component) => {
    if (!supplierId) {
      return null
    }

    return (props) => (
      <Component
        {...props}
        match={{
          ...props.match,
          params: {
            ...props.match.params,
            supplierId,
          },
        }}
      />
    )
  }


  const logo = partner === 'voicetome' ?
    'https://voicetome-branding.s3.eu-central-1.amazonaws.com/logo-white-white.png' :
    'https://voicearchive-branding.s3.eu-central-1.amazonaws.com/small-curve-white.svg'

  if (requireNda) {
    return (
      <Router history={history}>
        <SidebarLayout
          brand={{ name: '', logo }}
          notifications={[]}
          routes={[
            {
              name: formatMessage(messages.navContact),
                path: '/reload-dashboard',
                href: formatMessage(messages.navContactHref),
                icon: 'at',
            },
          ]}
        >
          <Nda match={{ params: { supplierId }}} />
        </SidebarLayout>
      </Router>
    )
  }

  return (
    <Router history={history}>
      <SidebarLayout
        brand={{ name: '', logo }}
        routes={nextRoutes}
        notifications={[]}
      >
        <div className="flex h-full w-full">
          <Switch>
            <Route exact path="/" component={SupplierDashboardPage} />
            <Route path="/ls/:hashId" component={PublicLiveSessionRedirectPage} />
            <Route path="/scheduling-poll/:schedulingPollHashId" component={PublicSchedulingPollPage} />
            <Route path="/memory-sharing/:hash" component={PublicMemorySharingPage} />
            <Route path="/talent/:supplierId/profile" component={SupplierProfile} />
            <Route path="/talent/profile" render={renderWithSupplierId(SupplierProfile)} />
            <Route path="/dashboard" component={SupplierDashboardPage} />
            <Route path="/data" render={renderWithSupplierId(SupplierDataTab)} />
            <Route path="/supplier/:supplierId/data" component={SupplierDataTab} />
            <Route path="/settings" component={UserSettingsPage} />
            <Route path="/docs" component={SupplierInfoPage} />
            <Route path="/bookings" render={renderWithSupplierId(SupplierBookingsPage)} />
            <Route path="/overview" component={SupplierJobOverviewPage} />
            <Route path="/ai-voice-clone" render={renderWithSupplierId(SupplierSyntheticVoicePage)} />

            <Route path="/jobs/:state" render={(props) => {
              const params = new URLSearchParams(props?.location?.search)
              if (params.get('state') == props.match.params.state) {
                return (<TaskSearchPage history={history} {...props} />)
              }
              if (props.match.isExact) {
                history.push({
                  pathname: `/jobs/${props.match.params.state}`,
                  search: "?" + new URLSearchParams({state: props.match.params.state}).toString()
                })
              }
            }} />
            <Route path={[
              "/orders/:orderId/jobs/:jobId/versions/:versionId/tasks/:taskId",
              "/orders/:orderId/jobs/:jobId/versions/:versionId",
              "/orders/:orderId/jobs/:jobId",
              "/orders/:orderId",
              "/order/:orderId",
            ]} component={SupplierTaskRedirectPage} />
            <Redirect from="/talent/sign-up" to="/talent/profile" />
            <Redirect from="/reload-dashboard" to="/dashboard" />
            <Redirect from="/demos" to="/talent/profile/demos" />
            <Redirect from="/question/:id" to="/task/:id" />
            <Redirect
              from={[
                "/orders",
                "/order"
              ]}
              to="/jobs/new"
            />
          </Switch>
          <Route path="/jobs/:state/:taskId" render={({ match }) => (
            <SupplierTaskPage history={history} taskId={match.params.taskId} />
          )} />
          <Route path="/task/:taskId"  render={({ match }) => {
            if (parseInt(match.params.taskId)) {
              return <SupplierTaskPage history={history} taskId={match.params.taskId} />
            } else {
              return null
            }
            
          }} />

          { partner === 'voicearchive' &&
            <SupplierChat />
          }
        </div>
      </SidebarLayout>
    </Router>
  )
}

export default SupplierApp
